"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import {
  getCases,
  getUserIdCases,
  getActiveServices,
  getUserRentalCases,
  getServiceById,
  downloadCasePdf,
  createInvoice,
  checkPaymentStatus,
  updatePaymentStatus,
  // ADDED: API functions for signing/declining lease
  signLease,
  // declineLease
} from "../services/api"
import CaseDetailsModal from "../components/CaseDetailsModal"
import "./MyWorkplacePage.css"

// ADDED: Imports for PDF generation
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

// Icons - replaced from lucide-react
const ChevronDown = () => <span>▼</span>
const ChevronRight = () => <span>►</span>
const PenLine = () => <span>📝</span>
const Layers = () => <span>📤</span>
const Clock = () => <span>⏳</span>
const Wallet = () => <span>💳</span>
const RotateCcw = () => <span>↩️</span>
const CheckCircle2 = () => <span>✅</span>
const XCircle = () => <span>❌</span>
const FolderOpen = () => <span>📁</span>
const Eye = () => <span>🔍</span>
const Download = () => <span>🔗</span>
const FileCheck = () => <span>✅📄</span>
const FileX = () => <span>❌📄</span>
const Home = () => <span>🏠</span>
const DollarSign = () => <span>💰</span>
const X = () => <span>✖️</span>
const REACT_APP_ZAMPAY_BASE_URL = process.env.REACT_APP_ZAMPAY_BASE_URL;
const REACT_APP_ZAMPAYPDF_BASE_URL = process.env.REACT_APP_ZAMPAYPDF_BASE_URL;



// --- ADDED: Lease Template HTML ---
const LEASE_TEMPLATE_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Lease Agreement</title>
  <style>
    /* Base styling */
    body {
      font-family: "Times New Roman", serif;
      margin: 40px;
      line-height: 1.6;
    }

    /* Remove page gaps */
    html, body {
      margin: 0;
      padding: 0;
    }

    * {
      page-break-before: avoid !important;
      page-break-after: avoid !important;
      page-break-inside: avoid !important;
    }

    p {
      margin: 8px 0;
    }

    br {
      line-height: 0;
    }

    h2, h3, h4 {
      text-align: center;
      margin-top: 10px;
      margin-bottom: 10px;
    }

    .center {
      text-align: center;
    }

    .section-title {
      font-weight: bold;
      margin-top: 20px;
      text-decoration: underline;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10px;
      border: 1px solid #000;
    }

    td, th {
      border: 1px solid #000;
      padding: 6px;
      vertical-align: top;
    }

    .signature-block {
      margin-top: 40px;
      width: 100%;
    }

    .signature-block td {
      width: 50%;
      vertical-align: top;
    }

    .schedule {
      border: 1px solid #000;
      padding: 10px;
      margin-top: 25px;
    }
  </style>
</head>
<body>
  
  <div class="center">
    <img src="/logozambia.png" alt="Government Logo" width="120">
  </div>
  
  <h2>THE REPUBLIC OF ZAMBIA</h2>
  <h3>LEASE AGREEMENT</h3>

  <p class="center"><strong>BETWEEN</strong></p>
  <p class="center">
    <strong>THE MINISTRY OF INFRASTRUCTURE, HOUSING AND URBAN DEVELOPMENT</strong>
  </p>

  <p class="center"><strong>AND</strong></p>
  <p class="center">
    <strong>{{tenant_name}}</strong> 
  </p>

  <p><strong>Relating to</strong> the premises located at <strong>{{property_location}}</strong>, measuring approximately <strong>{{plot_size}}</strong> hectares.</p>

  <p><strong>DATE:</strong> {{agreement_date}}</p>

  <p><strong>WHEREBY IT IS AGREED AS FOLLOWS:</strong></p>

  <p>
    In consideration of the rent reserved and the Tenant’s covenants herein contained,
    the Landlord hereby demises unto the Tenant the said property for a term of
    <strong>{{lease_duration}}</strong> commencing on the
    <strong>{{lease_start_date}}</strong> and expiring on the
    <strong>{{lease_end_date}}</strong> at an annual rent of
    <strong>{{rent_amount}}</strong> ZMW (inclusive of withholding tax), with an annual escalation of
    <strong>3%</strong> payable as follows:
  <p>(a) Rental for the first TWO (2) months of the term hereby created upon the 
    execution hereof by both parties; and
  </p>
  <p>(b) Thereafter every two (2) months on the first day of the month at ZMW {{rent_amount}} 
      per month inclusive of Withholding Tax.
  </p>

  <div class="section-title">1. TENANT’S COVENANTS</div>
  <ul>
    <li>To register this Lease Agreement at their own cost in accordance with Section 4 of the Lands and Deeds Registry Act.</li>
    <li>To pay rent and observe all covenants during the term.</li>
    <li>To promptly pay for utilities and service charges including electricity, water, and waste disposal.</li>
    <li>To maintain the premises in good tenantable repair except for fair wear and tear or unavoidable accidents.</li>
    <li>Not to alter, sublet, assign, or use the premises for illegal or immoral purposes without written consent from the Landlord.</li>
    <li>To allow access to the Landlord or its agents for inspections or necessary repairs.</li>
    <li>To yield up the premises in good condition at the end of the lease term.</li>
  </ul>

  <div class="section-title">2. LANDLORD’S COVENANTS</div>
  <ul>
    <li>To allow the Tenant quiet enjoyment of the demised premises during the lease term.</li>
    <li>To maintain structural repairs necessary for the continued use of the premises.</li>
  </ul>

  <div class="section-title">3. INSURANCE & DAMAGE</div>
  <p>
    In the event that the premises are damaged by fire or other peril and rendered unfit for occupation,
    rent shall be suspended wholly or in part until the premises are restored, provided the insurance
    policy has not been vitiated by the Tenant’s act or default.
  </p>
  
  <br><br>
  <br>

  <div class="section-title">4. TERMINATION & DEFAULT</div>
  <ul>
    <li>If rent remains unpaid for twenty-one (21) days after becoming due, the Landlord may re-enter and terminate the lease.</li>
    <li>If the Tenant becomes bankrupt or goes into liquidation, the Landlord may re-enter and terminate the lease.</li>
  </ul>

  <br><br>

  <div class="section-title">5. RENEWAL OPTION</div>
  <p>
    The Tenant may, not less than three (3) months and not more than one (1) year before the lease expires,
    apply in writing to renew the lease for a further term, subject to the Landlord’s approval.
  </p>

  <div class="section-title">6. DISPUTE RESOLUTION</div>
  <p>
    Any dispute arising from this Agreement shall be resolved through consultation and negotiation
    between the parties before resorting to court proceedings.
  </p>

  <div class="section-title">7. EXECUTION</div>
  <p>
    IN WITNESS WHEREOF the Parties hereto have set their hands and seals this
    <strong>{{signing_date}}</strong> day of <strong>{{signing_month_year}}</strong>.
  </p>

  <table class="signature-block">
    <tr>
      <td>
        <strong>THE LANDLORD</strong><br>
        Name: <strong>Ministry of Infrastructure and Urban Development</strong><br>
        Designation: <strong>{{landlord_title}}</strong><br>
        For and on behalf of the Ministry of Infrastructure, Housing and Urban Development acting for and On Behalf of the Government Of The Republic of Zambia<br>
        Signature: ___________________________<br>
        Date: {{landlord_sign_date}}
      </td>
      <td>
        <strong>THE TENANT</strong><br>
        Name: <strong>{{tenant_name}}</strong><br>
        Designation (if applicable): <strong>Tenant</strong><br>
        Signature: ___________________________<br>
        Date: {{tenant_sign_date}}
      </td>
    </tr>
  </table>

  <table class="signature-block">
    <tr>
      <td>
        <strong>WITNESS (Landlord)</strong><br>
        Name: ___________________________<br>
        Address: _________________________<br>
        Signature: _________________________
      </td>
      <td>
        <strong>WITNESS (Tenant)</strong><br>
        Name: ___________________________<br>
        Address: _________________________<br>
        Signature: _________________________
      </td>
    </tr>
  </table>

  <div class="schedule">
    <h4>FIRST SCHEDULE</h4>
    <p>Description of Land: {{property_location}}, measuring {{plot_size}} ha.</p>

    <h4>SECOND SCHEDULE</h4>
    <p>
      Tenant Name: {{tenant_name}}<br>
      Address: {{tenant_address}}<br>
      Lease Term: {{lease_duration}}<br>
      Start Date: {{lease_start_date}}<br>
      Rent: {{rent_amount}} ZMW inclusive of WHT<br>
    </p>
  </div>

</body>
</html>
`

// --- ADDED: Logic from lease.js ---

// Function to fetch the HTML template (modified to use the constant)
const getLeaseTemplate = async () => {
  return Promise.resolve(LEASE_TEMPLATE_HTML);
};

// Function to replace placeholders in the template
const fillTemplate = (template, data) => {
  let filledTemplate = template;
  for (const key in data) {
    const regex = new RegExp(`{{${key}}}`, 'g');
    filledTemplate = filledTemplate.replace(regex, data[key] || ''); // Ensure null/undefined becomes empty string
  }
  return filledTemplate;
};

const generateLeaseFromTemplate = async (caseDetails) => {
  try {
    const template = await getLeaseTemplate();

    // --- Data Mapping ---
    const caseData = caseDetails?.formData || {};
    const applicantData = caseDetails?.applicant || {};

    const data = {
      tenant_name: applicantData.name || 'N/A',
      tenant_address: applicantData.residentialAddress || 'N/A',
      property_location: caseData.textfield_hof5uk || 'N/A',
      stand_number: caseData.textfield_stand_number || 'N/A',
      plot_size: caseData.number_jh8o5q || 'N/A',
      agreement_date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      lease_duration: caseData.textfield_hbif9 || 'N/A',

      lease_start_date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),

      lease_end_date: (() => {
        let calculatedLeaseEndDate = 'N/A';
        const durationText = caseData.textfield_hbif9;
        const startDate = new Date();

        if (durationText) {
          const lowerCaseDuration = durationText.toLowerCase();

          if (lowerCaseDuration === 'ongoing') {
            calculatedLeaseEndDate = 'Ongoing';
          } else {
            const match = lowerCaseDuration.match(/(\d+)\s*(month|months|year|years)/);
            if (match) {
              const value = parseInt(match[1], 10);
              const unit = match[2];
              const endDate = new Date(startDate);

              if (unit.startsWith('month')) {
                endDate.setMonth(startDate.getMonth() + value);
              } else if (unit.startsWith('year')) {
                endDate.setFullYear(startDate.getFullYear() + value);
              }
              calculatedLeaseEndDate = endDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
            }
          }
        }
        return calculatedLeaseEndDate;
      })(),
      rent_amount: caseData.number_1y12gf || caseData.number_gol3p || caseData.number_1mxedt || 'N/A',
      payment_frequency: caseData.textfield_payment_frequency || 'N/A',
      escalation_rate: caseData.number_escalation_rate || 'N/A',
      signing_date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      signing_month_year: new Date().toLocaleString('default', { month: 'long', year: 'numeric' }),
      landlord_name: 'Permanent Secretary',
      landlord_title: 'Permanent Secretary',
      landlord_sign_date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      tenant_designation: 'N/A',
      tenant_sign_date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      premises_use: caseData.textfield_premises_use || 'N/A',
      bank_name: 'Zambia National Commercial Bank',
      account_number: '1234567890',
      payment_code: 'RENT'
    };

    const filledHtml = fillTemplate(template, data);

    // Create a temporary element to render the HTML
    const container = document.createElement('div');
    container.innerHTML = filledHtml;
    container.style.width = '210mm'; // A4 width
    container.style.padding = '20mm';
    document.body.appendChild(container);

    const canvas = await html2canvas(container, {
      scale: 2, // Higher scale for better quality
      useCORS: true,
      logging: true,
      windowHeight: container.scrollHeight
    });

    document.body.removeChild(container);

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const ratio = canvasWidth / canvasHeight;
    const imgHeight = pdfWidth / ratio;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position -= pdfHeight; // Decrement position by page height
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    const pdfBlob = pdf.output('bloburl');
    return pdfBlob;

  } catch (error) {
    console.error("Error generating lease from template:", error);
    throw error;
  }
};

// --- END of lease.js logic ---


// Loader component for invoice generation
const LoaderOverlay = ({ text = "Generating Invoice..." }) => ( // Modified to accept text
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    color: 'white',
    fontSize: '1.2rem',
    fontFamily: 'sans-serif'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        display: 'inline-block',
        width: '50px',
        height: '50px',
        border: '5px solid rgba(255, 255, 255, 0.3)',
        borderTopColor: '#fff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '1rem'
      }}></div>
      <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
      <p>{text}</p>
    </div>
  </div>
);

const Button = ({ className, onClick, children, ...rest }) => (
  <button className={className} onClick={onClick} {...rest}>
    {children}
  </button>
)

const Avatar = ({ className, children }) => <div className={className}>{children}</div>

const AvatarFallback = ({ className, children }) => <div className={className}>{children}</div>

const getInitials = (name) => {
  if (!name) return ""
  const names = name.split(" ")
  if (names.length === 1) return names[0].charAt(0).toUpperCase()
  return names[0].charAt(0).toUpperCase() + names[names.length - 1].charAt(0).toUpperCase()
}

const formatDate = (dateString) => {
  if (!dateString) return ""
  const date = new Date(dateString)
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const generateMockCaseNumber = (index) => {
  const date = new Date()
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const year = date.getFullYear()
  const serial = (index + 1).toString().padStart(4, "0")
  return `MIHUD-${serial}/${month}-${year}`
}

// FIX: Added caseId prop to update payment status
const InvoiceIframeModal = ({ isOpen, onClose, invoiceNumber, caseId }) => {
  const [paymentStatus, setPaymentStatus] = useState('pending'); // 'pending', 'complete', 'in_progress' (for button)
  const [docType, setDocType] = useState('invoice'); // 'invoice' or 'receipt'
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPaymentStatus('pending');
      setDocType('invoice');
      setIsButtonDisabled(false);
    }
  }, [isOpen]);

  const pdfBaseUrl = `${REACT_APP_ZAMPAYPDF_BASE_URL}/pdf?documentType=${docType}`;
  const iframeSrc = `${pdfBaseUrl}&invoiceNumber=${invoiceNumber}`;

  useEffect(() => {
    let pollInterval;

    if (isOpen && invoiceNumber && paymentStatus !== 'complete') {
      const checkStatus = async () => {
        try {
          const response = await checkPaymentStatus(invoiceNumber);
          const status = response.data?.status || response.data?.paymentStatus;

          if (status === 'Paid') {
            setPaymentStatus('complete');
            setDocType('receipt');
            clearInterval(pollInterval);

            // FIX: Call updatePaymentStatus when payment is confirmed
            if (caseId && invoiceNumber) {
              try {
                await updatePaymentStatus(caseId, invoiceNumber);
                console.log(`Payment status successfully updated for Case ID: ${caseId}`);
              } catch (updateError) {
                console.error("Failed to update payment status in backend:", updateError);
                // Optional: Add user feedback for the update failure
              }
            }
          }
        } catch (error) {
          console.error("Error checking invoice status:", error);
        }
      };

      pollInterval = setInterval(checkStatus, 5000);
    } else if (paymentStatus === 'complete' && pollInterval) {
      clearInterval(pollInterval);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [isOpen, invoiceNumber, paymentStatus, caseId]); // FIX: Added caseId to dependency array

  if (!isOpen || !invoiceNumber) return null;

  const handlePayment = () => {
    setPaymentStatus('in_progress');
    setIsButtonDisabled(true);
    const payBaseUrl = `${REACT_APP_ZAMPAY_BASE_URL}/pay-for-service/get-invoice`;
    const paymentUrl = `${payBaseUrl}/${invoiceNumber}`;
    window.open(paymentUrl, '_blank');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content invoice-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '70vw',
          height: '95vh',
          maxWidth: '95vw',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
        }}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            {docType === 'invoice' ? 'Invoice - #' : 'Receipt - #'}
            {invoiceNumber}
            {paymentStatus === 'complete' && <span style={{ marginLeft: '10px', color: 'green' }}>✅ Paid</span>}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <X />
          </button>
        </div>

        <iframe
          src={iframeSrc}
          title={`${docType} ${invoiceNumber} PDF`}
          width="100%"
          style={{
            border: "none",
            flexGrow: 1,
            minHeight: "400px"
          }}
        />

        <div className="modal-footer">
          {paymentStatus !== 'complete' && (
            <Button
              className="pay-button"
              onClick={handlePayment}
              type="button"
              disabled={isButtonDisabled || paymentStatus === 'in_progress'}
              style={{
                backgroundColor: isButtonDisabled || paymentStatus === 'in_progress' ? 'gray' : '',
                cursor: isButtonDisabled || paymentStatus === 'in_progress' ? 'not-allowed' : 'pointer',
              }}
            >
              <span>
                {paymentStatus === 'in_progress' ? (
                  <>⏳ Payment in Progress...</>
                ) : (
                  <>Pay</>
                )}
              </span>
            </Button>
          )}
          {paymentStatus === 'complete' && (
            <Button className="pay-button" onClick={onClose} style={{ backgroundColor: 'green' }}>
              <span>Payment Complete</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

// --- ADDED: LeaseAgreementModal Component ---
const LeaseAgreementModal = ({ isOpen, onClose, caseItem, onAccept, onDecline }) => {
  const [leasePdfUrl, setLeasePdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && caseItem) {
      const generateLease = async () => {
        setIsLoading(true);
        setError(null);
        setLeasePdfUrl(null);
        try {
          const pdfUrl = await generateLeaseFromTemplate(caseItem);
          setLeasePdfUrl(pdfUrl);
        } catch (err) {
          console.error("Failed to generate lease:", err);
          setError("Failed to generate lease agreement. Please try again.");
        } finally {
          setIsLoading(false);
        }
      };
      generateLease();
    } else {
      // Cleanup blob URL when modal is closed or caseItem is removed
      if (leasePdfUrl) {
        URL.revokeObjectURL(leasePdfUrl);
        setLeasePdfUrl(null);
      }
      setIsLoading(false);
      setError(null);
    }

    // Cleanup function
    return () => {
      if (leasePdfUrl) {
        URL.revokeObjectURL(leasePdfUrl);
      }
    };
  }, [isOpen, caseItem]); // Only re-run when these change

  const handleAccept = async () => {
    if (!caseItem || !caseItem.id) return;
    setIsSubmitting(true);
    try {
      await onAccept(caseItem.id);
      onClose(); // Close modal on success
    } catch (err) {
      console.error("Failed to accept lease:", err);
      setError("Failed to accept lease. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!caseItem || !caseItem.id) return;
    setIsSubmitting(true);
    try {
      await onDecline(caseItem.id);
      onClose(); // Close modal on success
    } catch (err) {
      console.error("Failed to decline lease:", err);
      setError("Failed to decline lease. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content lease-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '70vw',
          height: '95vh',
          maxWidth: '95vw',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
        }}
      >
        {isLoading && <LoaderOverlay text="Generating Lease Agreement..." />}

        <div className="modal-header">
          <h2 className="modal-title">Lease Agreement for Signing</h2>
          <button className="modal-close" onClick={onClose}>
            <X />
          </button>
        </div>

        <div style={{ flexGrow: 1, overflow: 'hidden' }}>
          {error && <div style={{ padding: '20px', color: 'red', textAlign: 'center' }}>{error}</div>}

          {leasePdfUrl && (
            <iframe
              src={leasePdfUrl}
              title="Lease Agreement PDF"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          )}
        </div>

        <div className="modal-footer">
          <Button
            className="action-button pay-button" // Re-using pay-button style
            onClick={handleAccept}
            disabled={isSubmitting || isLoading || error}
            style={{ backgroundColor: 'green', opacity: (isSubmitting || isLoading || error) ? 0.5 : 1 }}
          >
            {isSubmitting ? 'Accepting...' : 'Accept and Sign'}
          </Button>
        </div>
      </div>
    </div>
  );
};
// --- END of LeaseAgreementModal Component ---

// --- HELPER COMPONENT: Notification Badge ---
const CountBadge = ({ count }) => {
  if (!count || count <= 0) return null;
  return <span className="sidebar-badge">{count}</span>;
};

export default function MyWorkplacePage() {
  const { user } = useAuth()
  const [submittedExpanded, setSubmittedExpanded] = useState(true)
  const [completedExpanded, setCompletedExpanded] = useState(false)
  const [documentsExpanded, setDocumentsExpanded] = useState(false)
  const [rentalsExpanded, setRentalsExpanded] = useState(false)
  const [activeStatus, setActiveStatus] = useState("In Progress")
  const [displayCases, setDisplayCases] = useState([])
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false)
  const [selectedInvoiceCase, setSelectedInvoiceCase] = useState(null)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedCase, setSelectedCase] = useState(null)
  const [invoiceNumber, setInvoiceNumber] = useState(null)
  const [isInvoiceLoading, setIsInvoiceLoading] = useState(false)
  // FIX: Added state to hold the case ID for the invoice modal
  const [selectedCaseIdForInvoice, setSelectedCaseIdForInvoice] = useState(null)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // --- ADDED: State for new Lease Modal ---
  const [isLeaseModalOpen, setIsLeaseModalOpen] = useState(false)
  const [selectedCaseForLease, setSelectedCaseForLease] = useState(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0) // For refreshing list

  // --- NEW: State for Sidebar Counts ---
  const [sidebarCounts, setSidebarCounts] = useState({
    generalPayment: 0,
    generalReverted: 0,
    rentalSignature: 0,
    rentalPayment: 0,
    rentalRenewal: 0
  });

  const handleSidebarSelect = (action) => {
    action();
    if (window.innerWidth <= 768) {
      setIsSidebarOpen(false);
    }
  };

  // --- NEW: Fetch Counts Logic ---
  useEffect(() => {
    const fetchSidebarCounts = async () => {
      try {
        // We use Promise.all to fetch them in parallel so it loads fast
        const [
          genPaymentReq,
          genRevertedReq,
          rentalSignReq,
          rentalPayReq,
          // rentalRenewReq // Uncomment when you have a status for this
        ] = await Promise.all([
          getUserIdCases({ status: "AP Payment" }),
          getUserIdCases({ status: "Reverted" }), // Or "Requires Action" depending on your DB
          getUserRentalCases({ status: "AP Sign Lease" }),
          getUserRentalCases({ status: "AP Payment" }),
          // getUserRentalCases({ status: "Pending Renewal" }) // Example placeholder
        ]);

        setSidebarCounts({
          generalPayment: genPaymentReq.data.cases?.length || 0,
          generalReverted: genRevertedReq.data.cases?.length || 0,
          rentalSignature: rentalSignReq.data.cases?.length || 0,
          rentalPayment: rentalPayReq.data.cases?.length || 0,
          rentalRenewal: 0 // Set this to rentalRenewReq.data.cases?.length once API is ready
        });
      } catch (error) {
        console.error("Error fetching sidebar counts:", error);
      }
    };

    fetchSidebarCounts();
  }, [refreshTrigger]); // Re-fetch counts whenever the list refreshes (e.g. after signing a lease)

  // --- Swipe gesture handling for mobile ---
  useEffect(() => {
    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchEndX = null; // Reset
    };

    const handleTouchMove = (e) => {
      touchEndX = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
      if (touchEndX === null) return; // No move occurred

      const swipeDistance = touchEndX - touchStartX;

      // Swipe right (open sidebar)
      if (swipeDistance > 60 && !isSidebarOpen) {
        setIsSidebarOpen(true);
      }

      // Swipe left (close sidebar)
      if (swipeDistance < -60 && isSidebarOpen) {
        setIsSidebarOpen(false);
      }

      // Reset for safety
      touchEndX = null;
    };

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isSidebarOpen]);

  // Helper for General Cases (AP Payment, Reverted, etc.)
  const fetchGeneralCases = async (status, limit = 10) => {
    try {
      const response = await getUserIdCases({ status, limit });
      // Note: If your API supports a 'limit' param for general cases, pass it. 
      // Otherwise, it might default to 10.
      return response.data.cases || [];
    } catch (error) {
      console.error(`Failed to fetch ${status} cases:`, error);
      return []; // Return empty array on fail so app doesn't crash
    }
  };

  const fetchRentalCases = async (status) => {
    try {
      const response = await getUserRentalCases({
        page: 1,
        limit: 10,
        sortBy: "createdAt",
        sortOrder: "DESC",
        status
      })
      return response.data.cases || []
    } catch (error) {
      console.error("Failed to fetch rental cases:", error)
      return []
    }
  }

  useEffect(() => {
    const getCounts = async () => {
      // We use a higher limit (e.g., 100) for counts so the badge is accurate 
      // (otherwise it would cap at 10)
      const countLimit = 100;

      const [
        genPaymentCases,
        genRevertedCases,
        rentalSignCases,
        rentalPayCases
      ] = await Promise.all([
        fetchGeneralCases("AP Payment", countLimit),
        fetchGeneralCases("Reverted", countLimit),
        fetchRentalCases("AP Sign Lease", countLimit),
        fetchRentalCases("AP Payment", countLimit)
      ]);

      setSidebarCounts({
        generalPayment: genPaymentCases.length,
        generalReverted: genRevertedCases.length,
        rentalSignature: rentalSignCases.length,
        rentalPayment: rentalPayCases.length,
        rentalRenewal: 0 // Placeholder until you have a status for this
      });
    };

    getCounts();
  }, [refreshTrigger]); // Re-runs when you sign a lease or pay an invoice

  // --- 3. MAIN DISPLAY LOGIC (Uses the same helpers) ---
  useEffect(() => {
    const loadMainContent = async () => {
      if (activeStatus === "In Progress") {
        // Fetch multiple statuses for "In Progress"
        const results = await Promise.all([
          fetchGeneralCases("Submitted"),
          fetchGeneralCases("In Review"),
          fetchGeneralCases("Requires Action")
        ]);
        const allInProgress = [].concat(...results);

        // Add mock numbers if needed
        const withMock = allInProgress.map((c, index) => ({
          ...c,
          caseNumber: generateMockCaseNumber(index),
        }));
        setDisplayCases(withMock);

      } else if (activeStatus === "Pending Payment") {
        const cases = await fetchGeneralCases("AP Payment");
        setDisplayCases(cases);

      } else if (activeStatus === "Reverted") {
        const cases = await fetchGeneralCases("Reverted");
        setDisplayCases(cases);

      } else if (activeStatus === "Approved") {
        const cases = await fetchGeneralCases("Completed");
        setDisplayCases(cases);

      } else if (activeStatus.startsWith("Rentals-")) {
        // Handle Rental Tabs
        let cases = [];
        if (activeStatus === "Rentals-AppliedFor") {
          cases = await fetchRentalCases("Submitted");
        } else if (activeStatus === "Rentals-PendingSignature") {
          cases = await fetchRentalCases("AP Sign Lease");
        } else if (activeStatus === "Rentals-PendingPayment") {
          cases = await fetchRentalCases("AP Payment");
        } else if (activeStatus === "Rentals-History") {
          cases = await fetchRentalCases("Paid");
        }
        setDisplayCases(cases);

      } else {
        // Handle "Valid", "Expired", "Drafts", etc. using your Service logic
        getActiveServices({ type: "public" })
          .then((response) => {
            // ... your existing logic for drafts/services ...
            const fetchedServices = response.data;
            // (Assuming you have logic here to filter mock cases or drafts)
            setDisplayCases([]);
          })
          .catch(console.error);
      }
    };

    loadMainContent();
  }, [activeStatus, refreshTrigger]);

  const handleOpenInvoice = async (caseItem) => {
    if (!caseItem.id) {
      console.error("Case item missing ID for invoice creation.");
      return;
    }

    setIsInvoiceLoading(true);
    // FIX: Set the current case ID to be passed to the modal
    setSelectedCaseIdForInvoice(caseItem.id);

    try {
      let newInvoiceData = '';
      if (caseItem.invoicenumber) {
        newInvoiceData = caseItem.invoicenumber;
      } else {
        const response = await createInvoice(caseItem.id);
        newInvoiceData = response.data;
      }

      console.log(caseItem);
      console.log("New Invoice Data:", newInvoiceData);

      if (!newInvoiceData) {
        throw new Error("API did not return an invoice number.");
      }

      setInvoiceNumber(newInvoiceData);
      setIsInvoiceModalOpen(true);

    } catch (error) {
      console.error("Error creating invoice:", error.response ? error.response.data : error.message);
      setInvoiceNumber(null);
      alert(`Failed to create invoice: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
    } finally {
      setIsInvoiceLoading(false);
    }
  }

  const handleOpenDetails = async (caseItem) => {
    try {
      const serviceResponse = await getServiceById(caseItem.service.id);
      const fullService = serviceResponse.data;
      const caseWithSchema = {
        ...caseItem,
        service: fullService,
      };
      setSelectedCase(caseWithSchema);
      setIsDetailsModalOpen(true);
    } catch (error) {
      console.error("Error fetching service details for case:", error);
    }
  };

  // MODIFIED: handleOpenLease to open the new modal
  const handleOpenLease = async (caseItem) => {
    try {
      // The caseItem from the list (from getRentalCases) should have
      // the applicant and formData needed by the template generator.
      setSelectedCaseForLease(caseItem);
      setIsLeaseModalOpen(true);
    } catch (error) {
      console.error("Error opening lease modal:", error);
      alert("Failed to open lease. Please try again.");
    }
  };

  // --- ADDED: Handlers for Lease Actions ---
  const handleAcceptLease = async (caseId) => {
    try {
      await signLease(caseId);
      alert("Lease successfully signed!");
      setRefreshTrigger(prev => prev + 1); // Trigger refresh
    } catch (error) {
      console.error("Error signing lease:", error);
      alert(`Failed to sign lease: ${error.response?.data?.message || error.message}`);
      throw error; // Re-throw to keep modal open and show error
    }
  };

  // const handleDeclineLease = async (caseId) => {
  //   try {
  //       await declineLease(caseId);
  //       alert("Lease successfully declined.");
  //       setRefreshTrigger(prev => prev + 1); // Trigger refresh
  //   } catch (error) {
  //       console.error("Error declining lease:", error);
  //       alert(`Failed to decline lease: ${error.response?.data?.message || error.message}`);
  //       throw error; // Re-throw to keep modal open and show error
  //   }
  // };
  // --- END of Lease Action Handlers ---


  const handleDownloadForm = async (caseItem) => {
    if (caseItem && caseItem.id) {
      try {
        const response = await downloadCasePdf(caseItem.id);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `application-form-${caseItem.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading PDF for case:", caseItem.id, error);
      }
    } else {
      console.warn("No case ID available for PDF download for case:", caseItem);
    }
  };

  const isDocumentStatus = activeStatus === "Valid" || activeStatus === "Expired"
  // --- FIX: Renamed and updated logic to check both payment folders ---
  const isPendingPayment = activeStatus === "Pending Payment" || activeStatus === "Rentals-PendingPayment"
  const isRentalPendingSign = activeStatus === "Rentals-PendingSignature"

  return (
    <div className="my-workplace-page">
      {isInvoiceLoading && <LoaderOverlay />}
      {/* Note: The Lease modal has its own internal loader */}

      <div className="main-content">


        <aside className={`sidebar ${isSidebarOpen ? "open" : ""}`}>
          <div className="sidebar-user-profile">
            <div className="user-profile-content">
              <Avatar className="avatar">
                <AvatarFallback className="avatar-fallback">{user ? getInitials(user.name) : ""}</AvatarFallback>
              </Avatar>
              <div>
                <h2 className="user-name">{user ? user.name : "..."}</h2>
              </div>
            </div>
          </div>

          <nav className="sidebar-nav">
            <div className="sidebar-nav-items">
              <Button
                className={`sidebar-nav-button ${activeStatus === "Drafts" ? "sidebar-nav-button-active" : ""}`}
                onClick={() => handleSidebarSelect(() => setActiveStatus("Drafts"))}
              >
                <PenLine />
                <span>Drafts</span>
              </Button>

              <div>
                <Button className="sidebar-nav-button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSubmittedExpanded(!submittedExpanded); }}>
                  <Layers />
                  <span style={{ flex: 1, textAlign: "left" }}>Submitted</span>
                  {submittedExpanded ? <ChevronDown /> : <ChevronRight />}
                </Button>

                {submittedExpanded && (
                  <div className="sidebar-nav-nested">
                    <Button
                      className={`sidebar-nav-button ${activeStatus === "In Progress" ? "sidebar-nav-button-active" : ""}`}
                      onClick={() => handleSidebarSelect(() => setActiveStatus("In Progress"))}
                    >
                      <Clock />
                      <span>In Progress</span>
                    </Button>

                    <div className="sidebar-nav-nested">
                      {/* 1. General Pending Payment */}
                      <Button
                        className={`sidebar-nav-button ${activeStatus === "Pending Payment" ? "sidebar-nav-button-active" : ""}`}
                        onClick={() => handleSidebarSelect(() => setActiveStatus("Pending Payment"))}
                      >
                        <Wallet />
                        <span className="sidebar-text" style={{ marginRight: '8px' }}>Pending Payment</span>
                        <CountBadge count={sidebarCounts.generalPayment} />
                      </Button>

                      {/* 2. General Reverted */}
                      <Button
                        className={`sidebar-nav-button ${activeStatus === "Reverted" ? "sidebar-nav-button-active" : ""}`}
                        onClick={() => handleSidebarSelect(() => setActiveStatus("Reverted"))}
                      >
                        <RotateCcw />
                        <span className="sidebar-text" style={{ marginRight: '8px' }}>Reverted</span>
                        <CountBadge count={sidebarCounts.generalReverted} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Button className="sidebar-nav-button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCompletedExpanded(!completedExpanded); }}>
                  <CheckCircle2 />
                  <span style={{ flex: 1, textAlign: "left" }}>Completed</span>
                  {completedExpanded ? <ChevronDown /> : <ChevronRight />}
                </Button>
                {/* ... Nested Completed Buttons ... */}
                {completedExpanded && (
                  <div className="sidebar-nav-nested">
                    <Button className={`sidebar-nav-button ${activeStatus === "Approved" ? "sidebar-nav-button-active" : ""}`} onClick={() => handleSidebarSelect(() => setActiveStatus("Approved"))}>
                      <CheckCircle2 /><span>Approved</span>
                    </Button>
                    <Button className={`sidebar-nav-button ${activeStatus === "Rejected" ? "sidebar-nav-button-active" : ""}`} onClick={() => handleSidebarSelect(() => setActiveStatus("Rejected"))}>
                      <XCircle /><span>Rejected</span>
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <Button className="sidebar-nav-button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDocumentsExpanded(!documentsExpanded); }}>
                  <FolderOpen />
                  <span style={{ flex: 1, textAlign: "left" }}>Documents</span>
                  {documentsExpanded ? <ChevronDown /> : <ChevronRight />}
                </Button>
                {documentsExpanded && (
                  <div className="sidebar-nav-nested">
                    <Button
                      className={`sidebar-nav-button ${activeStatus === "Valid" ? "sidebar-nav-button-active" : ""}`}
                      onClick={() => handleSidebarSelect(() => setActiveStatus("Valid"))}
                    >
                      <FileCheck />
                      <span>Valid</span>
                    </Button>
                    <Button
                      className={`sidebar-nav-button ${activeStatus === "Expired" ? "sidebar-nav-button-active" : ""}`}
                      onClick={() => handleSidebarSelect(() => setActiveStatus("Expired"))}
                    >
                      <FileX />
                      <span>Expired</span>
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <Button className="sidebar-nav-button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRentalsExpanded(!rentalsExpanded); }}>
                  <Home />
                  <span style={{ flex: 1, textAlign: "left" }}>Rentals</span>
                  {rentalsExpanded ? <ChevronDown /> : <ChevronRight />}
                </Button>

                {rentalsExpanded && (
                  <div className="sidebar-nav-nested">
                    <Button
                      className={`sidebar-nav-button ${activeStatus === "Rentals-AppliedFor" ? "sidebar-nav-button-active" : ""}`}
                      onClick={() => handleSidebarSelect(() => setActiveStatus("Rentals-AppliedFor"))}
                    >
                      <Layers />
                      <span>Submitted</span>
                    </Button>

                    {/* 3. Rental Pending Signature */}
                    <Button
                      className={`sidebar-nav-button ${activeStatus === "Rentals-PendingSignature" ? "sidebar-nav-button-active" : ""}`}
                      onClick={() => handleSidebarSelect(() => setActiveStatus("Rentals-PendingSignature"))}
                    >
                      <PenLine />
                      <span className="sidebar-text" style={{ marginRight: '8px' }}>Pending Signature</span>
                      <CountBadge count={sidebarCounts.rentalSignature} />
                    </Button>

                    {/* 4. Rental Pending Payment */}
                    <Button
                      className={`sidebar-nav-button ${activeStatus === "Rentals-PendingPayment" ? "sidebar-nav-button-active" : ""}`}
                      onClick={() => handleSidebarSelect(() => setActiveStatus("Rentals-PendingPayment"))}
                    >
                      <Wallet />
                      <span className="sidebar-text" style={{ marginRight: '8px' }}>Pending Payments</span>
                      <CountBadge count={sidebarCounts.rentalPayment} />
                    </Button>

                    {/* 5. Rental Pending Renewal */}
                    <Button
                      className={`sidebar-nav-button ${activeStatus === "Rentals-RenewalPayments" ? "sidebar-nav-button-active" : ""}`}
                      onClick={() => handleSidebarSelect(() => setActiveStatus("Rentals-RenewalPayments"))}
                    >
                      <RotateCcw />
                      <span className="sidebar-text" style={{ marginRight: '8px' }}>Pending Renewal</span>
                      <CountBadge count={sidebarCounts.rentalRenewal} />
                    </Button>

                    <Button
                      className={`sidebar-nav-button ${activeStatus === "Rentals-History" ? "sidebar-nav-button-active" : ""}`}
                      onClick={() => handleSidebarSelect(() => setActiveStatus("Rentals-History"))}
                    >
                      <CheckCircle2 />
                      <span>Payment History</span>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </nav>
        </aside>
        {isSidebarOpen && (
          <div
            className="sidebar-overlay"
            onClick={() => setIsSidebarOpen(false)}
          ></div>
        )}

        {/* Hamburger menu for mobile */}
        <button
          className="hamburger-btn"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        >
          ☰
        </button>

        <main className="main-container">
          <div className="main-inner">
            <h1 className="main-title">
              {activeStatus === "Valid"
                ? "Valid Documents"
                : activeStatus === "Expired"
                  ? "Expired Documents"
                  : activeStatus === "Rentals-PendingPayment"
                    ? "Rental Pending Payments"
                    : `${activeStatus} Cases`}
            </h1>

            <div className="table-container">
              <div className="table-responsive">
                <table className="cases-table">
                  <thead className="table-head">
                    <tr>
                      {isDocumentStatus ? (
                        <>
                          <th className="table-header">Document Name</th>
                          <th className="table-header">Actions</th>
                        </>
                      ) : (
                        <>
                          <th className="table-header">Case Number</th>
                          <th className="table-header">Service Name</th>
                          <th className="table-header">Applied On</th>
                          <th className="table-header">Actions</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {displayCases.map((caseItem, index) => (
                      <tr key={index} className="table-row">
                        <td className="table-cell table-cell-medium" data-label="Case Number">
                          {caseItem.casenumber}
                        </td>
                        <td className="table-cell" data-label="Service Name">
                          {caseItem.service?.name || caseItem.serviceName}
                        </td>
                        <td className="table-cell table-cell-muted" data-label="Applied On">
                          {formatDate(caseItem.createdAt || caseItem.appliedOn)}
                        </td>
                        <td className="table-cell" data-label="Actions">
                          <div className="action-buttons">
                            <Button className="action-button" title="View details" onClick={() => handleOpenDetails(caseItem)}>
                              <Eye />
                            </Button>
                            {isRentalPendingSign && (
                              <Button className="action-button" title="Sign Lease" onClick={() => handleOpenLease(caseItem)}>
                                <PenLine />
                              </Button>
                            )}
                            {isPendingPayment && (
                              <Button className="action-button action-button-payment" onClick={() => handleOpenInvoice(caseItem)}>
                                <DollarSign />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="swipe-zone"></div>

        </main>
      </div>

      <InvoiceIframeModal
        isOpen={isInvoiceModalOpen}
        onClose={() => {
          setIsInvoiceModalOpen(false);
          setInvoiceNumber(null);
          setSelectedCaseIdForInvoice(null);
          setRefreshTrigger(prev => prev + 1);
        }}
        invoiceNumber={invoiceNumber}
        caseId={selectedCaseIdForInvoice}
      />

      <CaseDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        caseItem={selectedCase}
      />

      {/* --- ADDED: Render the new Lease Modal --- */}
      <LeaseAgreementModal
        isOpen={isLeaseModalOpen}
        onClose={() => setIsLeaseModalOpen(false)}
        caseItem={selectedCaseForLease}
        onAccept={handleAcceptLease}
      />
    </div>
  )
}